import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { governanceApiService, STATUSES } from '../../services/governanceApiService'
import type { GovernanceRow, GovernanceSave, PolicyRow, Register } from '../../services/governanceApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import AdminTabs from './AdminTabs'

const REGISTERS: { key: Register; label: string; hint: string }[] = [
  { key: 'risk', label: 'Risks', hint: 'Scored likelihood × impact (1–5 each). 15+ is reviewed quarterly; accepted risks are re-signed at review.' },
  { key: 'vendor', label: 'Vendors', hint: 'Subprocessors and their assurance. Attach each vendor\'s current SOC 2 / ISO report when you review it.' },
  { key: 'training', label: 'Training', hint: 'One record per person per course. The completion date starts a yearly renewal reminder.' },
  { key: 'decision', label: 'Decisions', hint: 'Security Officer decisions and sign-offs, re-signed yearly.' },
]

const card: React.CSSProperties = {
  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md,
}
const input: React.CSSProperties = {
  padding: spacing.xs, borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: fontSize.sm, boxSizing: 'border-box', width: '100%',
}
const primary: React.CSSProperties = {
  padding: `${spacing.xs} ${spacing.md}`, background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: 'pointer',
}
const secondary: React.CSSProperties = {
  padding: `${spacing.xs} ${spacing.md}`, background: 'transparent', color: colors.textSecondary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer',
}
const linkButton: React.CSSProperties = { background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', padding: 0 }

function day(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : '—'
}

function save(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Super-admin governance registers (SOC 2 CC1–CC3, CC9): policy acknowledgement against the deployed
 * version, and the risk, vendor, training and decision registers. The Compliance Center scheduler pushes a
 * reminder when a record's review comes due; this is where the review is recorded, with evidence.
 */
export default function AdminGovernancePage() {
  const [params, setParams] = useSearchParams()
  const register = (REGISTERS.find(r => r.key === params.get('register'))?.key ?? 'risk') as Register
  const [rows, setRows] = useState<GovernanceRow[]>([])
  const [policies, setPolicies] = useState<PolicyRow[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<GovernanceSave | null>(null)
  const [reviewing, setReviewing] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const [r, p] = await Promise.all([governanceApiService.list(), governanceApiService.policies()])
      setRows(r)
      setPolicies(p)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load governance')
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function act(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true)
    setError('')
    try { await fn(); after?.(); await load() }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Action failed') }
    finally { setBusy(false) }
  }

  const visible = rows.filter(r => r.register === register)
  const dueCount = (k: Register) => rows.filter(r => r.register === k && r.reviewDue).length
  const meta = REGISTERS.find(r => r.key === register)!

  return (
    <div>
      <AdminTabs />
      <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.md }}>Governance</h1>
      {error && <div role="alert" style={{ color: colors.error, marginBottom: spacing.md }}>{error}</div>}

      <Policies policies={policies} busy={busy} onAcknowledge={list => act(() => governanceApiService.acknowledge(list))} />

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, margin: `${spacing.lg} 0 ${spacing.sm}` }}>Registers</h2>
      <div role="tablist" aria-label="Registers" style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap', marginBottom: spacing.sm }}>
        {REGISTERS.map(r => (
          <button key={r.key} type="button" role="tab" aria-selected={r.key === register}
            onClick={() => { setParams({ register: r.key }); setEditing(null); setReviewing(null) }}
            style={{ ...secondary, ...(r.key === register ? { background: colors.primary, color: '#fff', borderColor: colors.primary } : {}) }}>
            {r.label}{dueCount(r.key) > 0 ? ` (${dueCount(r.key)} due)` : ''}
          </button>
        ))}
        <button type="button" style={{ ...primary, marginLeft: 'auto' }} disabled={busy}
          onClick={() => { setReviewing(null); setEditing(blank(register)) }}>
          Add {meta.label.toLowerCase().replace(/s$/, '')}
        </button>
      </div>
      <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginBottom: spacing.sm }}>{meta.hint}</div>

      {editing && editing.register === register && !editing.governanceRecordId && (
        <EditForm value={editing} busy={busy} onCancel={() => setEditing(null)}
          onSave={v => act(() => governanceApiService.save(v), () => setEditing(null))} />
      )}

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <thead>
            <tr style={{ textAlign: 'left', color: colors.textSecondary }}>
              {['Key', 'Title', register === 'risk' ? 'Score' : register === 'vendor' ? 'Tier' : 'Owner', 'Status', 'Last review', 'Next review', ''].map(h =>
                <th key={h} style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={7} style={{ padding: spacing.sm, color: colors.textSecondary }}>No records yet.</td></tr>}
            {visible.map(r => (
              <RowView key={r.governanceRecordId} row={r} busy={busy}
                editing={editing?.governanceRecordId === r.governanceRecordId ? editing : null}
                reviewing={reviewing === r.governanceRecordId}
                onEdit={() => { setReviewing(null); setEditing(toSave(r)) }}
                onReview={() => { setEditing(null); setReviewing(r.governanceRecordId) }}
                onCancel={() => { setEditing(null); setReviewing(null) }}
                onSave={v => act(() => governanceApiService.save(v), () => setEditing(null))}
                onSubmitReview={(notes, file) => act(() => governanceApiService.review(r.governanceRecordId, notes, file), () => setReviewing(null))}
                onDownload={async () => {
                  try { save(await governanceApiService.evidence(r.governanceRecordId), r.evidenceFileName ?? 'evidence') }
                  catch (err) { setError(err instanceof ApiError ? err.message : 'Download failed') }
                }} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function blank(register: Register): GovernanceSave {
  return {
    register, recordKey: '', title: '', description: '', owner: register === 'training' ? '' : 'Security Officer',
    status: STATUSES[register][0], reviewIntervalDays: register === 'risk' ? 365 : 365,
    likelihood: register === 'risk' ? 3 : null, impact: register === 'risk' ? 3 : null,
    treatment: register === 'risk' ? 'mitigate' : null, tier: register === 'vendor' ? 2 : null, assurance: null,
    completedAt: register === 'training' ? new Date().toISOString().slice(0, 10) : null,
  }
}

function toSave(r: GovernanceRow): GovernanceSave {
  return {
    governanceRecordId: r.governanceRecordId, register: r.register, recordKey: r.recordKey, title: r.title,
    description: r.description, owner: r.owner, status: r.status, likelihood: r.likelihood, impact: r.impact,
    treatment: r.treatment, tier: r.tier, assurance: r.assurance, reviewIntervalDays: r.reviewIntervalDays,
  }
}

function RowView({ row: r, busy, editing, reviewing, onEdit, onReview, onCancel, onSave, onSubmitReview, onDownload }: {
  row: GovernanceRow
  busy: boolean
  editing: GovernanceSave | null
  reviewing: boolean
  onEdit: () => void
  onReview: () => void
  onCancel: () => void
  onSave: (v: GovernanceSave) => void
  onSubmitReview: (notes: string, file: File | null) => void
  onDownload: () => void
}) {
  const third = r.register === 'risk' ? `${r.score ?? '—'} (${r.likelihood}×${r.impact}, ${r.treatment})`
    : r.register === 'vendor' ? `${r.tier ?? '—'}` : (r.owner ?? '—')
  return (
    <>
      <tr style={{ borderTop: `1px solid ${colors.border}`, verticalAlign: 'top' }}>
        <td style={{ padding: spacing.sm, whiteSpace: 'nowrap', fontWeight: 600 }}>{r.recordKey}</td>
        <td style={{ padding: spacing.sm }}>
          <div style={{ color: colors.text }}>{r.title}</div>
          {r.description && <div style={{ color: colors.textSecondary }}>{r.description}</div>}
          {r.assurance && <div style={{ color: colors.textSecondary }}>Assurance: {r.assurance}</div>}
          {r.reviewNotes && <div style={{ color: colors.textSecondary, fontStyle: 'italic' }}>Last review: {r.reviewNotes}</div>}
          {r.evidenceFileName && <button type="button" style={linkButton} onClick={onDownload}>{r.evidenceFileName}</button>}
        </td>
        <td style={{ padding: spacing.sm, whiteSpace: 'nowrap', color: r.score != null && r.score >= 15 ? colors.error : colors.textSecondary }}>{third}</td>
        <td style={{ padding: spacing.sm }}>{r.status}</td>
        <td style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>{day(r.lastReviewedAt)}</td>
        <td style={{ padding: spacing.sm, whiteSpace: 'nowrap', color: r.reviewDue ? colors.error : colors.textSecondary }}>
          {r.reviewDue ? 'Due ' : ''}{day(r.nextReviewAt)}
        </td>
        <td style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>
          <button type="button" style={{ ...linkButton, marginRight: spacing.sm }} disabled={busy} onClick={onReview}>
            {r.register === 'training' ? 'Renew' : r.register === 'decision' ? 'Re-sign' : 'Review'}
          </button>
          <button type="button" style={linkButton} disabled={busy} onClick={onEdit}>Edit</button>
        </td>
      </tr>
      {(editing || reviewing) && (
        <tr>
          <td colSpan={7} style={{ padding: spacing.sm }}>
            {editing && <EditForm value={editing} busy={busy} onCancel={onCancel} onSave={onSave} />}
            {reviewing && <ReviewForm row={r} busy={busy} onCancel={onCancel} onSubmit={onSubmitReview} />}
          </td>
        </tr>
      )}
    </>
  )
}

function ReviewForm({ row, busy, onCancel, onSubmit }: {
  row: GovernanceRow
  busy: boolean
  onCancel: () => void
  onSubmit: (notes: string, file: File | null) => void
}) {
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  return (
    <div style={{ ...card, background: colors.background }}>
      <textarea aria-label={`Review notes for ${row.recordKey}`} rows={3} value={notes} onChange={e => setNotes(e.target.value)} style={input}
        placeholder={row.register === 'vendor' ? 'Report period, exceptions noted, CUECs we meet' : row.register === 'risk' ? 'Re-scored? Treatment progressing? Acceptance re-signed?' : 'What was reviewed or completed'} />
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap', marginTop: spacing.sm }}>
        <input type="file" aria-label={`Evidence for ${row.recordKey}`} onChange={e => setFile(e.target.files?.[0] ?? null)} />
        <button type="button" style={primary} disabled={busy || (!notes.trim() && !file)} onClick={() => onSubmit(notes, file)}>Record review</button>
        <button type="button" style={secondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function EditForm({ value, busy, onCancel, onSave }: {
  value: GovernanceSave
  busy: boolean
  onCancel: () => void
  onSave: (v: GovernanceSave) => void
}) {
  const [v, setV] = useState<GovernanceSave>(value)
  const set = <K extends keyof GovernanceSave>(k: K, val: GovernanceSave[K]) => setV(prev => ({ ...prev, [k]: val }))
  const isNew = !v.governanceRecordId
  const field = (label: string, el: React.ReactNode) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: fontSize.sm, color: colors.textSecondary }}>{label}{el}</label>
  )
  const score = (k: 'likelihood' | 'impact') => (
    <select aria-label={k} value={v[k] ?? 3} onChange={e => set(k, Number(e.target.value))} style={input}>
      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  )
  return (
    <form style={{ ...card, background: colors.background, marginBottom: spacing.sm }}
      onSubmit={e => { e.preventDefault(); onSave({ ...v, completedAt: v.completedAt ? `${v.completedAt.slice(0, 10)}T00:00:00Z` : null }) }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: spacing.sm }}>
        {field('Key', <input required maxLength={80} value={v.recordKey} onChange={e => set('recordKey', e.target.value)} style={input}
          placeholder={v.register === 'risk' ? 'R-26' : v.register === 'training' ? '2026-security-awareness' : 'short-key'} />)}
        {field(v.register === 'training' ? 'Course' : 'Title', <input required maxLength={300} value={v.title} onChange={e => set('title', e.target.value)} style={input} />)}
        {field(v.register === 'training' ? 'Person' : 'Owner', <input maxLength={100} required={v.register === 'training'} value={v.owner ?? ''} onChange={e => set('owner', e.target.value)} style={input} />)}
        {field('Status', <select value={v.status} onChange={e => set('status', e.target.value)} style={input}>
          {STATUSES[v.register].map(s => <option key={s} value={s}>{s}</option>)}
        </select>)}
        {v.register === 'risk' && <>
          {field('Likelihood', score('likelihood'))}
          {field('Impact', score('impact'))}
          {field('Treatment', <select value={v.treatment ?? 'mitigate'} onChange={e => set('treatment', e.target.value)} style={input}>
            {['mitigate', 'accept', 'transfer', 'avoid'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>)}
        </>}
        {v.register === 'vendor' && <>
          {field('Tier', <select value={v.tier ?? 2} onChange={e => set('tier', Number(e.target.value))} style={input}>
            <option value={1}>1 — critical</option><option value={2}>2 — high</option><option value={3}>3 — low</option>
          </select>)}
          {field('Assurance', <input maxLength={300} value={v.assurance ?? ''} onChange={e => set('assurance', e.target.value)} style={input} placeholder="SOC 2 Type II, period to …" />)}
        </>}
        {v.register === 'training' && isNew && field('Completed on',
          <input type="date" required value={v.completedAt?.slice(0, 10) ?? ''} onChange={e => set('completedAt', e.target.value)} style={input} />)}
        {field('Review every (days)', <input type="number" min={1} max={1100} required value={v.reviewIntervalDays}
          onChange={e => set('reviewIntervalDays', Number(e.target.value))} style={input} />)}
      </div>
      <div style={{ marginTop: spacing.sm }}>
        {field('Description', <textarea rows={3} maxLength={4000} value={v.description ?? ''} onChange={e => set('description', e.target.value)} style={input} />)}
      </div>
      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.sm }}>
        <button type="submit" style={primary} disabled={busy}>{isNew ? 'Add' : 'Save'}</button>
        <button type="button" style={secondary} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

function Policies({ policies, busy, onAcknowledge }: {
  policies: PolicyRow[]
  busy: boolean
  onAcknowledge: (list: { name: string; sha256: string }[]) => void
}) {
  const [confirmed, setConfirmed] = useState(false)
  const pending = policies.filter(p => !p.acknowledgedByMeAt)
  return (
    <>
      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, margin: `0 0 ${spacing.sm}` }}>Policies</h2>
      <div style={{ ...card, marginBottom: spacing.sm }}>
        {pending.length === 0
          ? <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>You have acknowledged the current version of every policy.</div>
          : <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: fontSize.sm, color: colors.text, display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                I have read and will comply with the {pending.length} polic{pending.length === 1 ? 'y' : 'ies'} not yet acknowledged
              </label>
              <button type="button" style={primary} disabled={busy || !confirmed}
                onClick={() => { onAcknowledge(pending.map(p => ({ name: p.name, sha256: p.sha256 }))); setConfirmed(false) }}>
                Acknowledge
              </button>
            </div>}
      </div>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <thead>
            <tr style={{ textAlign: 'left', color: colors.textSecondary }}>
              {['Policy', 'Effective', 'Version', 'You', 'Acknowledged by'].map(h => <th key={h} style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {policies.map(p => (
              <tr key={p.name} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: spacing.sm }}>{p.title}<div style={{ color: colors.textSecondary }}>{p.name}</div></td>
                <td style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>{p.effective ?? '—'}</td>
                <td style={{ padding: spacing.sm, fontFamily: 'monospace' }} title={p.sha256}>{p.sha256.slice(0, 8)}</td>
                <td style={{ padding: spacing.sm, whiteSpace: 'nowrap', color: p.acknowledgedByMeAt ? colors.success : colors.error }}>
                  {p.acknowledgedByMeAt ? day(p.acknowledgedByMeAt) : p.previousVersionAcknowledged ? 'Changed since you acknowledged' : 'Not acknowledged'}
                </td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{p.acknowledgements.map(a => a.email ?? a.userId).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
